(ns client-portal.client.voice-insights.upsell.views
  (:require [client-portal.client.layout :as l]
            [client-portal.client.voice-insights.upsell.data]
            [client-portal.client.voice-insights.charts.views :as vi.charts]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.util :as util]
            [shared.core.reactive :as re]))

(def outcome-labels {"accept" "Accept" "decline" "Decline"})

(defn upsell-event-chart [{:keys [tag-value title]}]
  [vi.charts/chart-card
   {:chart-id (str tag-value "-by-value")
    :chart-type "BarChart"
    :title title
    :stack-id "time_value"
    :name-key "time_value"
    :key-for-data-keys "value_counts"
    :data-keys ["accept" "decline"]
    :required-tag-names ["upsell-event"]
    :convert-values-to-percents? true
    :show-time-period-toggle? true
    :include-day-part-time-period? true
    :include-session-double-click? true
    :session-double-click-config {:open-utterances? true}
    :add-listener-dispatch? true
    :trend-keys ["accept"]
    :filter-tags-fn (fn [d {:keys [time-filter]}]
                      (let [value (or (some-> (:tooltip-state* d) deref :hovered-key)
                                      (:dataKey d)
                                      (:name d))]
                        (filterv identity
                                 [{:by "tags"
                                   :condition "include"
                                   :join "or"
                                   :value [{:name "upsell-event"
                                            :value tag-value}]
                                   :children (when value
                                               [{:by "outcome"
                                                 :condition "include"
                                                 :join "or"
                                                 :value [value]}])}
                                  time-filter])))
    :config-overrides {:data-keys-config {"accept" {:fill "#6DD9BD"}
                                          "decline" {:fill "#F5B7B1"}}
                       :trend-config {:stroke "#1F9C82"}
                       :tooltip-config {:label-transf-fn (fn [k] (get outcome-labels k k))
                                        :show-percent? true}
                       :legend-config {:display-fn (fn [k] (get outcome-labels k k))}
                       :margin {:top 20 :right 30 :left 20 :bottom 25}
                       :legend? true}}])

(defn upsell-accept-decline-chart [{:keys [tag-value title]}]
  (let [accept-decline-total (fn [r]
                               (+ (get r "accept" 0) (get r "decline" 0)))
        accept-decline-ratio (fn [outcome-key]
                               (fn [r]
                                 (let [total (accept-decline-total r)]
                                   (if (pos? total)
                                     (/ (get r outcome-key 0) total)
                                     0))))]
    [vi.charts/chart-card
     {:chart-id (str tag-value "-accept-decline")
      :chart-type "BarChart"
      :title title
      :stack-id "outcome"
      :name-key "name"
      :key-for-data-keys "value_counts"
      :data-keys ["accept" "decline"]
      :required-tag-names ["upsell-event"]
      :include-session-double-click? true
      :session-double-click-config {:open-utterances? true}
      :add-listener-dispatch? true
      :sort-options [{:label "Count" :value "count" :sort-fn accept-decline-total}
                     {:label "Accept %" :value "accept" :sort-fn (accept-decline-ratio "accept")}
                     {:label "Decline %" :value "decline" :sort-fn (accept-decline-ratio "decline")}]
      :threshold-filter {:value-fn accept-decline-total
                         :label "Only show items offered"
                         :unit "time"
                         :auto-limit-top-n 20
                         :empty-text "No data for the specified offered limit"}
      ;; Double-click a menu-item's accept/decline segment -> sessions for that menu-item + outcome.
      :filter-tags-fn (fn [d _]
                        (let [payload (or (:payload d) d)
                              menu-item (:name payload)
                              outcome (or (some-> (:tooltip-state* d) deref :hovered-key)
                                          (:dataKey d))]
                          (when (and menu-item outcome)
                            {:by "tags"
                             :condition "include"
                             :join "or"
                             :value [{:name "upsell-event"
                                      :value tag-value}]
                             :children [{:by "menu-item"
                                         :condition "include"
                                         :join "or"
                                         :value [menu-item]}
                                        {:by "outcome"
                                         :condition "include"
                                         :join "or"
                                         :value [outcome]}]})))
      :config-overrides {:data-keys-config {"accept" {:fill "#6DD9BD"}
                                            "decline" {:fill "#F5B7B1"}}
                         :tooltip-config {:label-transf-fn (fn [k] (get outcome-labels k k))
                                          :show-percent? true}
                         :legend-config {:display-fn (fn [k] (get outcome-labels k k))}
                         :margin {:top 20 :right 30 :left 20 :bottom 50}
                         :legend? false}}]))

(defn upsell-rates-chart [_]
  (let [labels {"pct_sessions_with_upsell_event" "Sessions with Upsell Attempt"
                "pct_sessions_with_successful_upsell" "Sessions with Upsell Success"
                "pct_upsell_event_success_rate" "Upsell Attempt Success"}]
    [vi.charts/chart-card
     {:chart-id "upsell-rates-by-time"
      :chart-type "LineChart"
      :title "All Upsell"
      :name-key "time_value"
      :data-keys ["pct_sessions_with_upsell_event"
                  "pct_sessions_with_successful_upsell"
                  "pct_upsell_event_success_rate"]
      :required-tag-names ["upsell-prompt"]
      :show-time-period-toggle? true
      :include-day-part-time-period? true
      :add-listener-dispatch? true
      :config-overrides {:colors {"pct_sessions_with_upsell_event" "#4C9AFF"
                                  "pct_sessions_with_successful_upsell" "#6DD9BD"
                                  "pct_upsell_event_success_rate" "#F5A623"}
                         :legend-label-fn (fn [k] (get labels k k))
                         :y-axis-config {:value-transf-fn (fn [p] (str (js/Math.round (:value p)) "%"))}
                         :tooltip-config {:label-transf-fn (fn [k] (get labels k k))
                                          :value-transf-fn (fn [v] (str v "%"))}}}]))

(defn root [_]
  (let [tags- (re/subscribe [:vi-tags/list-by-current-concept-id-])
        upsell-tags-config [{:title "Greeting Add-On Upsell"
                             :tag-value "upsell-greeting"}
                            {:title "Add-On Upsell"
                             :tag-value "upsell-add-on"}
                            {:title "Combo Upsell"
                             :tag-value "upsell-combo"}
                            {:title "Combo Upsize"
                             :tag-value "upsell-combo-size"}
                            {:title "Drink Upsize"
                             :tag-value "upsell-drink-size"}
                            {:title "Item Upsize"
                             :tag-value "upsell-item-size"}]
        chart-ids-loading?- (re/subscribe [:voice-insights/charts-loading?- (concat (mapv #(str (:tag-value %)
                                                                                                "-by-value")
                                                                                          upsell-tags-config)
                                                                                    (mapv #(str (:tag-value %)
                                                                                                "-accept-decline")
                                                                                          upsell-tags-config)
                                                                                    ["upsell-rates-by-time"])])]
    (re/create-class
      {:component-did-mount #(re/dispatch [:vi-upsell/initialize])
       :reagent-render
       (fn [args]
         @chart-ids-loading?-
         [l/logged-in-with-header-nav-vi
          args
          [l/standard-content-container {:title "Upsell"}
           (if (or (util/nil-or-loading? @tags-) @chart-ids-loading?-)
             [empty-state/loading-charts-card]
             (into [:div {:style {:display :flex
                                  :flex-direction :column
                                  :gap 12
                                  :width "100%"}}
                    [l/section-header {}
                     "Upsell Trends"]]
                   (concat (mapv (fn [conf]
                                   [upsell-event-chart conf])
                                 upsell-tags-config)
                           [[l/section-header {}
                             "Items/Upsell Performance"]]
                           (mapv (fn [conf]
                                   [upsell-accept-decline-chart conf])
                                 upsell-tags-config)
                           [[l/section-header {}
                             "Upsell Rates"]
                            [upsell-rates-chart {}]])))]])})))
